import React, { useState, useEffect } from 'react';
import { Layout, MessageSquare, User, Settings, CreditCard, LogOut, Palette } from 'lucide-react';
import ChatPage from './ChatPage';
import LoginPage from './LoginPage';
import UserSettingsPage from './UserSettingsPage';
import PricingPage from './PricingPage';
import DesignUploadPage from './DesignUploadPage';
import MarketplaceSetupPage from './MarketplaceSetupPage';
import { shouldRedirectToPricing, checkSubscriptionStatus } from '../services/subscriptionService';
// Legacy auth imports removed - using Entra External ID via MSAL only
import { AuthInterceptor } from '../services/authInterceptor';
import { useAuth } from '../hooks/useAuth';

/**
 * Loading Screen Component
 *
 * Unified loading screen for authentication, profile loading, and subscription checks
 */
const LoadingScreen: React.FC<{ message?: string }> = ({ message = "Loading..." }) => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  );
};

const AdvisioAddin: React.FC = () => {
  const [currentPage, setCurrentPage] = useState('chat');
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isOfficeInitialized, setIsOfficeInitialized] = useState(false);
  const [officeError, setOfficeError] = useState<string | null>(null);
  const [compactMode, setCompactMode] = useState(false);
  const [ultraCompactMode, setUltraCompactMode] = useState(false);

  // User state
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userSettings, setUserSettings] = useState<any>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Usage tracking state
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [slidesUsed, setSlidesUsed] = useState(0);
  const [slidesLimit, setSlidesLimit] = useState(0);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);

  // Subscription provider
  const [subscriptionProvider, setSubscriptionProvider] = useState<'stripe' | 'marketplace' | null>(null);

  // Marketplace setup
  const [marketplaceToken, setMarketplaceToken] = useState<string | null>(null);

  // New Entra External ID authentication
  const { isAuthenticated, user, userProfileLoaded, isLoading: authIsLoading, logout: entraLogout } = useAuth();

  // Sync user email from Entra External ID
  useEffect(() => {
    if (isAuthenticated && user) {
      setUserEmail(user.email);
    } else {
      setUserEmail(null);
    }
  }, [isAuthenticated, user]);

  // Check subscription status once after authentication and profile load
  useEffect(() => {
    // Skip subscription check during marketplace setup flow
    if (marketplaceToken) {
      setSubscriptionChecked(true);
      return;
    }
    // Only check if authenticated, profile loaded, and not yet checked
    if (isAuthenticated && userProfileLoaded && !subscriptionChecked) {
      const checkSubscription = async () => {
        setIsLoadingSubscription(true);
        try {
          await checkSubscriptionAndRedirect();
        } catch (error) {
          console.error('Error during subscription check:', error);
        } finally {
          setIsLoadingSubscription(false);
          setSubscriptionChecked(true);
        }
      };

      checkSubscription();
    }
  }, [isAuthenticated, userProfileLoaded, subscriptionChecked]);

  // Handle URL parameters on mount (Stripe redirect + Marketplace token)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('success');
    const paymentCanceled = urlParams.get('canceled');
    const sessionId = urlParams.get('session_id');
    const mpToken = urlParams.get('token');

    // Detect Marketplace token
    if (mpToken && window.location.pathname.includes('marketplace-setup')) {
      setMarketplaceToken(mpToken);
      setCurrentPage('marketplace-setup');
    }

    // Detect any Stripe-related query parameters
    const hasStripeParams = paymentSuccess || paymentCanceled || sessionId ||
                           window.location.search.includes('stripe');

    // Clean URL parameters immediately to prevent persistence
    if (hasStripeParams && typeof window !== 'undefined' && window.history && typeof window.history.replaceState === 'function') {
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.warn('Could not clean URL parameters:', error);
      }
    }

    // Handle payment success - re-check subscription and redirect accordingly
    if (paymentSuccess === 'true' && isAuthenticated && userProfileLoaded) {
      const handlePaymentSuccess = async () => {
        setIsLoadingSubscription(true);
        try {
          // Enable retry for payment success to wait for webhook processing
          await checkSubscriptionAndRedirect(true);
        } catch (error) {
          console.error('Error checking subscription after payment:', error);
        } finally {
          setIsLoadingSubscription(false);
        }
      };
      handlePaymentSuccess();
    }

    // Handle Customer Portal return (session_id or other Stripe params)
    if ((sessionId || hasStripeParams) && isAuthenticated && !paymentSuccess && !paymentCanceled && userProfileLoaded) {
      // Re-check subscription status after Customer Portal actions
      const handlePortalReturn = async () => {
        setIsLoadingSubscription(true);
        try {
          // Enable retry for portal return to wait for webhook processing
          await checkSubscriptionAndRedirect(true);
        } catch (error) {
          console.error('Error checking subscription after portal return:', error);
        } finally {
          setIsLoadingSubscription(false);
        }
      };
      handlePortalReturn();
    }
  }, [isAuthenticated, userProfileLoaded]);

  // Register global 401 unauthorized handler
  useEffect(() => {
    AuthInterceptor.setUnauthorizedHandler(() => {
      // Clear local state
      setUserEmail(null);
      setUserSettings(null);
      setSubscriptionChecked(false);
    });
  }, []);

  // Token refresh is handled automatically by MSAL via acquireTokenSilent()

  // Helper function to check subscription and redirect if needed
  const checkSubscriptionAndRedirect = async (shouldRetry = false, retryCount = 0, maxRetries = 3): Promise<void> => {
    try {
      const status = await checkSubscriptionStatus();
      if (!status || !status.has_active_subscription) {
        // If retry is enabled and we haven't exceeded max retries, retry (webhook might still be processing)
        if (shouldRetry && retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return checkSubscriptionAndRedirect(shouldRetry, retryCount + 1, maxRetries);
        }
        // Otherwise, show pricing page
        setHasActiveSubscription(false);
        setSubscriptionProvider(null);
        setIsReadOnly(false);
        setSlidesUsed(0);
        setSlidesLimit(0);
        setPeriodEnd(null);
        setCurrentPage('pricing');
      } else {
        // User has active subscription - update usage state and go to chat
        setHasActiveSubscription(true);
        setSubscriptionProvider(status.provider || 'stripe');
        setIsReadOnly(status.is_read_only);
        setSlidesUsed(status.slides_used);
        setSlidesLimit(status.slides_limit);
        setPeriodEnd(status.period_end);
        setCurrentPage('chat');
      }
    } catch (error) {
      console.error('Error checking subscription status on load:', error);
      // Retry on error if retry is enabled and we haven't exceeded max retries
      if (shouldRetry && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return checkSubscriptionAndRedirect(shouldRetry, retryCount + 1, maxRetries);
      }
      // Don't change page on error after max retries or if retry is disabled
    }
  };

  // Function to refresh usage status (called after slide creation or 429 error)
  const refreshUsageStatus = async (): Promise<void> => {
    try {
      const status = await checkSubscriptionStatus();
      if (status && status.has_active_subscription) {
        setIsReadOnly(status.is_read_only);
        setSlidesUsed(status.slides_used);
        setSlidesLimit(status.slides_limit);
        setPeriodEnd(status.period_end);
      }
    } catch (error) {
      console.error('Error refreshing usage status:', error);
    }
  };

  // Save user email to localStorage
  useEffect(() => {
    if (userEmail) {
      localStorage.setItem('advisio_user_email', userEmail);
    } else {
      localStorage.removeItem('advisio_user_email');
    }
  }, [userEmail]);

  // Detect if we're in a narrow add-in context with optimized resize handling
  useEffect(() => {
    let rafId: number | null = null;

    const checkWidth = () => {
      // Cancel any pending animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      // Use requestAnimationFrame for smooth, optimized updates during resize
      rafId = requestAnimationFrame(() => {
        const width = window.innerWidth;

        // Ultra-compact for very narrow PowerPoint taskpanes (< 380px)
        if (width < 380) {
          setUltraCompactMode(true);
          setCompactMode(true);
        }
        // Compact mode for narrow spaces (< 500px)
        else if (width < 500) {
          setUltraCompactMode(false);
          setCompactMode(true);
        }
        // Normal mode for wider spaces
        else {
          setUltraCompactMode(false);
          setCompactMode(false);
        }

        rafId = null;
      });
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);

    return () => {
      window.removeEventListener('resize', checkWidth);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Initialize Office.js
  useEffect(() => {
    const initializeOffice = async () => {
      try {
        // Check if Office is available
        if (typeof Office !== 'undefined') {
          await Office.onReady((info) => {
            console.log('Office.js initialized successfully');
            console.log('Host:', info.host);
            console.log('Platform:', info.platform);
            setIsOfficeInitialized(true);

            // Hide loading indicator if it exists
            const loadingEl = document.getElementById('loading-indicator');
            if (loadingEl) {
              loadingEl.style.display = 'none';
            }
          });
        } else {
          // For development/testing outside of Office
          console.log('Office.js not available - running in development mode');
          setIsOfficeInitialized(true);

          // Hide loading indicator if it exists
          const loadingEl = document.getElementById('loading-indicator');
          if (loadingEl) {
            loadingEl.style.display = 'none';
          }
        }
      } catch (error) {
        console.error('Error initializing Office.js:', error);
        setOfficeError('Failed to initialize Office Add-in');
        setIsOfficeInitialized(true); // Still allow the app to load
      }
    };

    initializeOffice();
  }, []);

  // Function to handle page navigation with subscription check
  const navigateTo = async (page: string) => {
    // Allow navigation to pricing page without subscription check
    if (page === 'pricing') {
      setCurrentPage(page);
      return;
    }

    // For all other pages, check subscription status
    const needsPricing = await shouldRedirectToPricing();
    if (needsPricing) {
      // Redirect to pricing if no active subscription
      setCurrentPage('pricing');
      return;
    }

    // User has active subscription, allow navigation
    setCurrentPage(page);
  };

  // Authentication handler functions

  // Legacy handleLogin removed - authentication handled by MSAL/useAuth



  const handleSettingsUpdate = (newSettings: any) => {
    setUserSettings(newSettings);
    // Update user email if it changed
    if (newSettings.workEmail && newSettings.workEmail !== userEmail) {
      setUserEmail(newSettings.workEmail);
      localStorage.setItem('advisio_user_email', newSettings.workEmail);
    }
  };

  const handleLogout = async () => {
    // Set logging out state to show loading screen
    setIsLoggingOut(true);

    // Clear local state
    setUserEmail(null);
    setUserSettings(null);
    setSubscriptionChecked(false);

    // Logout from Entra External ID (MSAL will handle token cleanup and redirect)
    await entraLogout();

    // Note: This line likely won't execute because entraLogout redirects
    // But included as fallback
    setIsLoggingOut(false);
  };




  // Render the main content based on authentication state
  const renderMainContent = () => {
    // 1. Show loading during logout
    if (isLoggingOut) {
      return <LoadingScreen message="Signing out..." />;
    }

    // 2. Show loading while authentication system is initializing
    if (authIsLoading) {
      return <LoadingScreen message="Loading..." />;
    }

    // 3. Show loading while user profile is being fetched (after authentication)
    if (isAuthenticated && !userProfileLoaded) {
      return <LoadingScreen message="Loading..." />;
    }

    // 4. Show loading while subscription status is being checked
    if (isAuthenticated && userProfileLoaded && isLoadingSubscription) {
      return <LoadingScreen message="Loading..." />;
    }

    // 5. User is not authenticated - show login page
    if (!isAuthenticated) {
      return (
        <LoginPage
          navigateTo={navigateTo}
        />
      );
    }

    // 6. User is fully authenticated and initialized - show page content
    return renderPageContent();
  };

  // Render page content based on current page
  const renderPageContent = () => {
    switch(currentPage) {
      case 'chat':
        return (
          <ChatPage
            navigateTo={navigateTo}
            compactMode={compactMode}
            ultraCompactMode={ultraCompactMode}
            isReadOnly={isReadOnly}
            slidesUsed={slidesUsed}
            slidesLimit={slidesLimit}
            periodEnd={periodEnd}
            onUsageUpdate={refreshUsageStatus}
          />
        );
      case 'settings':
        return (
          <UserSettingsPage
            navigateTo={navigateTo}
            userEmail={userEmail}
            onSettingsUpdate={handleSettingsUpdate}
          />
        );
      case 'marketplace-setup':
        return (
          <MarketplaceSetupPage
            navigateTo={navigateTo}
            marketplaceToken={marketplaceToken || ''}
          />
        );
      case 'pricing':
        return (
          <PricingPage
            navigateTo={navigateTo}
            userEmail={userEmail}
            trialStatus={null} // TODO: 後でトライアルステータスを統合
            hasActiveSubscription={hasActiveSubscription}
            subscriptionProvider={subscriptionProvider}
            onUpgrade={(planId) => {
              console.log('Upgrading to plan:', planId);
              // TODO: Stripe統合
            }}
          />
        );
      case 'design':
        return (
          <DesignUploadPage
            navigateTo={navigateTo}
          />
        );
      default:
        return (
          <ChatPage
            navigateTo={navigateTo}
            compactMode={compactMode}
            ultraCompactMode={ultraCompactMode}
            isReadOnly={isReadOnly}
            slidesUsed={slidesUsed}
            slidesLimit={slidesLimit}
            periodEnd={periodEnd}
            onUsageUpdate={refreshUsageStatus}
          />
        );
    }
  };

  // Show loading screen while Office.js initializes
  if (!isOfficeInitialized) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="mb-4">
            <svg width="48" height="48" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto animate-pulse">
              <path d="M16 4L4 16L16 28L28 16L16 4Z" fill="#1E56E5" />
              <path d="M16 10L10 16L16 22L22 16L16 10Z" fill="#0D99FF" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Initializing Advisio</h2>
          <p className="text-sm text-gray-500">Loading PowerPoint Add-in...</p>
          {officeError && (
            <p className="text-sm text-red-500 mt-2">{officeError}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-screen bg-gray-50 overflow-hidden ${compactMode ? 'text-sm' : ''}`}>
        {/* Simplified Sidebar */}
        {sidebarVisible && (
        <div
          className={`bg-white border-r shadow-sm overflow-y-auto flex flex-col transition-width ${
            ultraCompactMode ? 'w-10 sm:w-12' : compactMode ? 'w-14 sm:w-16' : 'w-16 sm:w-20'
          }`}
        >
          {/* Logo */}
          <div className="p-1.5 sm:p-2 border-b flex justify-center">
            <svg width={ultraCompactMode ? "14" : compactMode ? "18" : "22"} height={ultraCompactMode ? "14" : compactMode ? "18" : "22"} viewBox="0 0 375 374.999991" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <defs>
                <clipPath id="5e228ab043">
                  <path d="M 23.988281 232 L 350.988281 232 L 350.988281 365 L 23.988281 365 Z M 23.988281 232 " clipRule="nonzero"/>
                </clipPath>
                <clipPath id="eeee442b5b">
                  <path d="M 76 232 L 350.988281 232 L 350.988281 365 L 76 365 Z M 76 232 " clipRule="nonzero"/>
                </clipPath>
                <clipPath id="63b2c3a547">
                  <path d="M 23.988281 232 L 188 232 L 188 308 L 23.988281 308 Z M 23.988281 232 " clipRule="nonzero"/>
                </clipPath>
                <clipPath id="94d327d7e5">
                  <path d="M 76 10 L 350.988281 10 L 350.988281 143 L 76 143 Z M 76 10 " clipRule="nonzero"/>
                </clipPath>
                <clipPath id="e0b9948d46">
                  <path d="M 23.988281 67 L 188 67 L 188 143 L 23.988281 143 Z M 23.988281 67 " clipRule="nonzero"/>
                </clipPath>
              </defs>
              <g clipPath="url(#5e228ab043)">
                <path fill="#1e57a3" d="M 238.792969 232.34375 L 187.414062 283.726562 L 136.03125 232.34375 L 23.972656 232.34375 L 134.644531 343.011719 C 163.675781 372.039062 211.171875 372.039062 240.199219 343.011719 L 350.863281 232.34375 Z M 238.792969 232.34375 " fillOpacity="1" fillRule="evenodd"/>
              </g>
              <g clipPath="url(#eeee442b5b)">
                <path fill="#1e57a3" d="M 187.402344 283.726562 C 156.78125 314.359375 119.929688 314.863281 76.847656 285.230469 L 134.625 343.011719 C 163.652344 372.039062 211.152344 372.039062 240.179688 343.011719 L 350.839844 232.34375 L 238.78125 232.34375 Z M 187.402344 283.726562 " fillOpacity="1" fillRule="evenodd"/>
              </g>
              <g clipPath="url(#63b2c3a547)">
                <path fill="#007aff" d="M 76.859375 285.230469 C 119.9375 314.863281 156.792969 314.359375 187.414062 283.726562 L 136.035156 232.34375 L 23.972656 232.34375 L 76.871094 285.242188 Z M 76.859375 285.230469 " fillOpacity="1" fillRule="evenodd"/>
              </g>
              <g clipPath="url(#94d327d7e5)">
                <path fill="#1e57a3" d="M 187.402344 91.324219 C 156.78125 60.691406 119.929688 60.1875 76.847656 89.816406 L 134.625 32.039062 C 163.652344 3.007812 211.152344 3.007812 240.179688 32.039062 L 350.851562 142.707031 L 238.792969 142.707031 C 238.792969 142.707031 187.414062 91.324219 187.414062 91.324219 Z M 187.402344 91.324219 " fillOpacity="1" fillRule="evenodd"/>
              </g>
              <g clipPath="url(#e0b9948d46)">
                <path fill="#007aff" d="M 76.859375 89.816406 C 119.9375 60.1875 156.792969 60.691406 187.414062 91.324219 L 136.035156 142.707031 L 23.972656 142.707031 C 23.972656 142.707031 76.871094 89.808594 76.871094 89.808594 Z M 76.859375 89.816406 " fillOpacity="1" fillRule="evenodd"/>
              </g>
            </svg>
          </div>

          {/* Navigation Icons */}
          <div className="flex-1 flex flex-col">
            {isAuthenticated && hasActiveSubscription && (
              <>
                {/* Chat - Only show if user has active subscription */}
                <button
                  onClick={() => navigateTo('chat')}
                  className={`p-2 sm:p-3 flex justify-center items-center border-b transition-colors ${
                    currentPage === 'chat' ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Chat with AI"
                >
                  <MessageSquare size={ultraCompactMode ? 14 : compactMode ? 18 : 22} className="flex-shrink-0" />
                </button>
                {/* Design - Brand theme management */}
                <button
                  onClick={() => navigateTo('design')}
                  className={`p-2 sm:p-3 flex justify-center items-center border-b transition-colors ${
                    currentPage === 'design' ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Brand Design"
                >
                  <Palette size={ultraCompactMode ? 14 : compactMode ? 18 : 22} className="flex-shrink-0" />
                </button>
              </>
            )}
          </div>

          {/* Status & Controls */}
          {isAuthenticated ? (
            <div className="border-t p-1 sm:p-2 flex flex-col items-center space-y-1 sm:space-y-2">
              {/* User Status */}
              <div className="w-full space-y-1">
                {/* Settings - Only show if user has active subscription */}
                {hasActiveSubscription && (
                  <button
                    onClick={() => navigateTo('settings')}
                    className={`flex items-center justify-center w-full p-1.5 sm:p-2 rounded transition-colors ${
                      ultraCompactMode ? 'text-xs' : 'text-sm'
                    } ${currentPage === 'settings' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    title="User Settings"
                  >
                    <Settings size={ultraCompactMode ? 10 : compactMode ? 12 : 14} className="flex-shrink-0" />
                  </button>
                )}
                {/* Pricing - Always show */}
                <button
                  onClick={() => navigateTo('pricing')}
                  className={`flex items-center justify-center w-full p-1.5 sm:p-2 rounded transition-colors ${
                    ultraCompactMode ? 'text-xs' : 'text-sm'
                  } ${currentPage === 'pricing' ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                  title="Pricing Plans"
                >
                  <CreditCard size={ultraCompactMode ? 10 : compactMode ? 12 : 14} className="flex-shrink-0" />
                </button>
                <button
                  onClick={handleLogout}
                  className={`flex items-center justify-center w-full p-1.5 sm:p-2 rounded transition-colors ${
                    ultraCompactMode ? 'text-xs' : 'text-sm'
                  } bg-red-50 text-red-600 hover:bg-red-100`}
                  title="Sign Out"
                >
                  <LogOut size={ultraCompactMode ? 10 : compactMode ? 12 : 14} className="flex-shrink-0" />
                </button>
              </div>

              {/* Hide Sidebar Button */}
              {!ultraCompactMode && (
                <button
                  onClick={() => setSidebarVisible(false)}
                  className="text-[10px] sm:text-xs text-gray-500 hover:text-gray-700"
                  title="Hide Sidebar"
                >
                  ◀
                </button>
              )}
            </div>
          ) : (
            <div className="border-t p-1 sm:p-2" />
          )}
        </div>
      )}

      {/* Show Sidebar Button (when hidden) */}
      {!sidebarVisible && (
        <button
          onClick={() => setSidebarVisible(true)}
          className="fixed top-2 sm:top-4 left-2 sm:left-4 z-50 bg-white border shadow-md rounded-md p-1.5 sm:p-2 hover:bg-gray-50 text-sm sm:text-base"
          title="Show Sidebar"
        >
          ▶
        </button>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto min-w-0">
        {renderMainContent()}
      </div>
    </div>
  );
};

export default AdvisioAddin;
